/**
 * @file 预约专用充电站卡片组件
 * @description 简化版充电站卡片，只显示站点名称、设备信息和预约按钮
 */

import React from "react";
import EquipTypeTag from "@/component/station-item/components/equip-type-tag";
import { goToNativePage } from "@/utils/common";
import "@/component/station-item/index.less";
import "./index.less";

/** 设备概览接口 */
export interface EquipmentOverview {
  type: string;
  typeName: string;
  chargePowerType: number;
  freeNums: number;
  nums: number;
}

/** 充电站数据接口 */
export interface StationData {
  stationId: number;
  stationName: string;
  stationAddr?: string;
  distance?: number;
  stationEquipOverViewList?: EquipmentOverview[];
  stationAvailabilityDesc?: string;
}

export interface BookingStationItemProps {
  /** 充电站数据 */
  station: StationData;
  /** 距离（km） */
  distance?: number;
  /** 是否禁用（站点不可用） */
  disabled?: boolean;
  /** 右侧按钮渲染 */
  renderButton?: () => React.ReactNode;
  /** 点击卡片回调 */
  onClick?: () => void;
}

/**
 * 跳转到充电站详情页
 * @param stationId - 充电站 ID
 */
const goToStationDetail = (stationId: number | string) => {
  const { origin } = window.location;
  goToNativePage({
    app: `xiaopeng://charging/station/detail?id=${stationId}`,
    mini: `/charging/pages/station-detail/index?stationId=${stationId}`,
    browser: `${origin}/apps/charge-web/jump-to-wx-mini.html?page=charging/pages/station-detail/index&query=stationId%3D${stationId}`,
  });
};

/**
 * 预约专用充电站卡片组件
 * 简化版设计：站点名称 + 设备信息（距离、类型、空闲数） + 预约按钮
 */
const BookingStationItem: React.FC<BookingStationItemProps> = ({
  station,
  distance,
  disabled = false,
  renderButton,
  onClick,
}) => {
  const isDisabled = disabled || !!station?.stationAvailabilityDesc;
  const equipList = station?.stationEquipOverViewList || [];

  /**
   * 处理卡片点击 - 跳转到站点详情
   */
  const handleClick = () => {
    if (onClick) {
      onClick();
    }
    goToStationDetail(station.stationId);
  };

  return (
    <div
      className={`station-card-wrap booking-station-item ${isDisabled ? "disabled" : ""}`}
    >
      {/* 站点名称 + 距离 */}
      <div className="title-row">
        <span className="name">{station?.stationName}</span>
        {distance !== undefined && (
          <div className="distance-info">
            <img
              className="distance-icon"
              alt=""
              src="https://s.xiaopeng.com/xp-charge/mini/common/navigator-pointer.png"
            />
            <span className="distance-text">{distance}km</span>
          </div>
        )}
      </div>

      {/* 设备信息行：设备类型标签 + 预约按钮 */}
      <div className="booking-pile-info-row">
        <div className="pile-info-left">
          {/* 设备列表 */}
          {equipList.map((equip, index) => (
            <div className="equip-item" key={`${equip.type}-${index}`}>
              <EquipTypeTag
                type={equip.chargePowerType}
                name={equip.typeName}
                isDisabled={isDisabled}
              />
              <div className={`equip-status ${isDisabled ? "disabled" : ""}`}>
                <span className="idle-text">闲</span>
                <span className="idle-count">{equip.freeNums}</span>
                <span className="total-count">/{equip.nums}</span>
              </div>
            </div>
          ))}
        </div>
        {/* 右侧按钮 - 点击不冒泡到卡片 */}
        {renderButton && (
          <div className="pile-info-right" onClick={handleClick}>
            {renderButton()}
          </div>
        )}
      </div>

      {/* 不可用提示 */}
      {station?.stationAvailabilityDesc && (
        <div className="error-tip">{station.stationAvailabilityDesc}</div>
      )}
    </div>
  );
};

export default BookingStationItem;
